import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Car, FilePlus, Home, Image, Inbox, LayoutDashboard, LogOut, Menu, MessageSquare, Search, Settings, Tag, X } from "lucide-react";
import { useAdmin } from "@/admin/AdminAuth";
import { useNoIndex } from "@/lib/seo";
import { cn } from "@/lib/utils";

const MENU = [
  ["/admin", "Dashboard", LayoutDashboard, true],
  ["/admin/vehicles", "Vehicles", Car],
  ["/admin/vehicles/new", "Add Vehicle", FilePlus],
  ["/admin/leads/inquiry", "Vehicle Requests", Inbox],
  ["/admin/leads/sourcing", "Sourcing Requests", Search],
  ["/admin/leads/sell", "Sell Your Vehicle Requests", Tag],
  ["/admin/leads/contact", "Messages", MessageSquare],
  ["/admin/media", "Media", Image],
  ["/admin/homepage", "Homepage Content", Home],
  ["/admin/settings", "Site Settings", Settings],
];

export const AdminLayout = () => {
  const { user, logout } = useAdmin();
  const [open, setOpen] = useState(false);
  useNoIndex();
  return (
    <div className="admin lg:flex" data-testid="admin-layout">
      <aside className={cn("fixed inset-y-0 left-0 z-40 w-64 border-r border-white/10 bg-[#0b0d10] p-6 transition-transform lg:static lg:translate-x-0", open ? "translate-x-0" : "-translate-x-full")}>
        <Link to="/" className="block"><p className="display text-lg">Palissiere</p><p className="mono text-[0.55rem] text-[#8b95a5]">Motor Group · Admin</p></Link>
        <nav className="mt-10 space-y-1">
          {MENU.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => cn("flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors", isActive ? "bg-white/10 text-white" : "text-[#9aa3b0] hover:bg-white/5 hover:text-white")}>
              <Icon size={15} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-10 border-t border-white/10 pt-6">
          <p className="truncate text-xs text-[#8b95a5]">{user?.email}</p>
          <button onClick={logout} data-testid="admin-logout" className="mt-3 flex items-center gap-2 text-xs text-[#c7ccd4] hover:text-white"><LogOut size={13} /> Sign out</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="min-h-screen flex-1">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 lg:hidden">
          <button onClick={() => setOpen((o) => !o)} data-testid="admin-menu-toggle">{open ? <X /> : <Menu />}</button>
          <span className="mono text-[0.6rem]">Admin</span>
        </div>
        <div className="p-5 md:p-10"><Outlet /></div>
      </div>
    </div>
  );
};

export const PageHead = ({ title, children }) => (
  <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
    <h1 className="display-tight text-2xl md:text-3xl" data-testid="admin-page-title">{title}</h1>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const AField = ({ label, children, className }) => (
  <label className={cn("block", className)}>
    <span className="mb-1.5 block text-xs text-[#8b95a5]">{label}</span>
    {children}
  </label>
);

export const BiField = ({ label, value, onChange, textarea, rows = 3 }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {["fr", "en"].map((l) => (
      <AField key={l} label={`${label} (${l.toUpperCase()})`}>
        {textarea ? (
          <textarea className="field" rows={rows} value={value?.[l] || ""} onChange={(e) => onChange({ ...(value || {}), [l]: e.target.value })} data-testid={`${label.toLowerCase().replace(/\s+/g, "-")}-${l}`} />
        ) : (
          <input className="field" value={value?.[l] || ""} onChange={(e) => onChange({ ...(value || {}), [l]: e.target.value })} data-testid={`${label.toLowerCase().replace(/\s+/g, "-")}-${l}`} />
        )}
      </AField>
    ))}
  </div>
);

export const Btn = ({ children, className, variant = "primary", ...p }) => (
  <button className={cn("btn !py-2 !px-3.5 !text-[0.62rem]", variant === "primary" ? "btn-primary" : variant === "danger" ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "btn-ghost", className)} {...p}>{children}</button>
);
